import { api, tokenManager } from './api';
import type {
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  RegisterResponse,
} from '@travel-planner/shared';

interface AuthResponseData {
  success: boolean;
  data: AuthResponse;
}

interface RegisterResponseData {
  success: boolean;
  data: RegisterResponse;
}

interface MessageResponseData {
  success: boolean;
  data: { message: string };
}

interface MeResponseData {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      name: string;
    };
  };
}

export const authService = {
  /**
   * Register a new user. Does NOT log in — the user must verify their email
   * first. Returns the confirmation message + the email that was registered.
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponseData>('/auth/register', data);
    return response.data.data;
  },

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponseData>('/auth/login', data);
    const { accessToken, refreshToken, user } = response.data.data;

    // Store tokens
    tokenManager.setTokens(accessToken, refreshToken);

    return { accessToken, refreshToken, user };
  },

  /**
   * Verify an email using the token from the verification link. On success the
   * user is auto-logged-in (tokens are stored) and the user profile is returned.
   */
  async verifyEmail(token: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponseData>('/auth/verify-email', { token });
    const { accessToken, refreshToken, user } = response.data.data;

    tokenManager.setTokens(accessToken, refreshToken);

    return { accessToken, refreshToken, user };
  },

  /**
   * Request a password reset email. Always resolves with a generic message.
   */
  async forgotPassword(email: string): Promise<string> {
    const response = await api.post<MessageResponseData>('/auth/forgot-password', { email });
    return response.data.data.message;
  },

  /**
   * Reset the password using the token from the reset link.
   */
  async resetPassword(token: string, password: string): Promise<string> {
    const response = await api.post<MessageResponseData>('/auth/reset-password', {
      token,
      password,
    });
    return response.data.data.message;
  },

  /**
   * Logout (clears all sessions)
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      tokenManager.clearTokens();
    }
  },

  /**
   * Get current user profile
   */
  async getMe(): Promise<{ id: string; email: string; name: string }> {
    const response = await api.get<MeResponseData>('/auth/me');
    return response.data.data.user;
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    return Boolean(tokenManager.getAccessToken());
  },
};
